import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Line, LineChart, ResponsiveContainer, Tooltip } from "recharts";
import { AlertTriangle } from "lucide-react";
import type { UsageRow } from "@/hooks/useAdminMetrics";

export function ErrorsCard({ usage }: { usage: UsageRow[] }) {
  const { ok, errors, errorRate, sparkline, recent } = useMemo(() => {
    let ok = 0;
    let errors = 0;
    const byDay = new Map<string, { ok: number; err: number }>();
    for (const r of usage) {
      const day = r.created_at.slice(0, 10);
      const cur = byDay.get(day) ?? { ok: 0, err: 0 };
      if (r.status === "ok") {
        ok += 1;
        cur.ok += 1;
      } else {
        errors += 1;
        cur.err += 1;
      }
      byDay.set(day, cur);
    }
    const total = ok + errors;
    const errorRate = total ? (errors / total) * 100 : 0;

    // 7-day sparkline of errors
    const days: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      days.push(d);
    }
    const sparkline = days.map((d) => ({ day: d, errors: byDay.get(d)?.err ?? 0 }));

    const recent = usage
      .filter((r) => r.status !== "ok")
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .slice(0, 10);

    return { ok, errors, errorRate, sparkline, recent };
  }, [usage]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4" />
          Operational health
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Success</div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums text-emerald-600">{ok}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Errors</div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums text-destructive">{errors}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rate</div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">
              {errorRate.toFixed(2)}%
            </div>
          </div>
        </div>
        <div className="h-[60px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline}>
              <Line
                type="monotone"
                dataKey="errors"
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                dot={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload as { day: string; errors: number };
                  return (
                    <div className="rounded-md border bg-popover p-2 text-xs shadow-md">
                      <div className="font-mono">{p.day}</div>
                      <div className="text-destructive">{p.errors} errors</div>
                    </div>
                  );
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Last {recent.length} errors
          </h4>
          {recent.length === 0 ? (
            <p className="rounded border border-dashed p-3 text-center text-xs text-muted-foreground">
              No errors in this period. 🎉
            </p>
          ) : (
            <div className="space-y-1.5 text-xs">
              {recent.map((r) => (
                <div key={r.id} className="rounded border p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                    <span className="text-[10px]">
                      {r.call_type} · {r.model.replace("gemini-2.5-", "")}
                    </span>
                  </div>
                  {r.error ? (
                    <p className="mt-1 break-words text-[11px] text-destructive">{r.error}</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
